import { useEffect, useRef } from "react";
import { NATIVE_BACK_EVENT } from "../services/platform/lifecycle";

/** Shared dismissal/focus handling for dialogs and sheets, including Android Back. */
export function useDialog<T extends HTMLElement>(open: boolean, onClose?: () => void) {
  const ref = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const scrollRegion = dialog.closest<HTMLElement>(".app-content");
    const previousScrollOverflow = scrollRegion?.style.overflowY;
    document.body.style.overflow = "hidden";
    if (scrollRegion) scrollRegion.style.overflowY = "hidden";
    const inertSiblings: Array<{ element: HTMLElement; previous: boolean }> = [];
    let branch: HTMLElement | null = dialog;
    while (branch?.parentElement) {
      for (const element of branch.parentElement.children) {
        if (element !== branch && element instanceof HTMLElement) {
          inertSiblings.push({ element, previous: element.inert });
          element.inert = true;
        }
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
    )).filter((element) => element.getClientRects().length > 0);
    if (!dialog.contains(document.activeElement)) (focusable()[0] ?? dialog).focus();
    const isTop = () => Array.from(document.querySelectorAll('[role="dialog"]')).at(-1) === dialog;
    const close = () => { if (isTop()) closeRef.current?.(); };
    const keydown = (event: KeyboardEvent) => {
      if (!isTop()) return;
      if (event.key === "Escape") { event.preventDefault(); close(); }
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener(NATIVE_BACK_EVENT, close);
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener(NATIVE_BACK_EVENT, close);
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      if (scrollRegion) scrollRegion.style.overflowY = previousScrollOverflow ?? "";
      inertSiblings.forEach(({ element, previous }) => { element.inert = previous; });
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [open]);
  return ref;
}
